import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useLanguage } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { isAuthenticated } from '../../utils/tokenManager';

const LANGUAGE_OPTIONS = [
  { code: 'ar', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'en', nativeName: 'English', flag: '🇺🇸' },
  { code: 'zh', nativeName: '中文', flag: '🇨🇳' },
  { code: 'hi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ha', nativeName: 'Hausa', flag: '🇳🇬' },
  { code: 'ru', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'es', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'tr', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'fr', nativeName: 'Français', flag: '🇫🇷' },
];

export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLanguage = (code: string) => {
    void (async () => {
      const previous = currentLanguage;
      changeLanguage(code);
      setOpen(false);

      if (!isAuthenticated()) return;

      try {
        await updateProfile({ language: code }).unwrap();
      } catch {
        changeLanguage(previous);
        toast.error(t('navigation.languageSaveError'));
      }
    })();
  };

  const current = LANGUAGE_OPTIONS.find((l) => l.code === currentLanguage) || LANGUAGE_OPTIONS[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={isLoading}
        className="relative inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg bg-neutral-dark/40 text-neutral-light border border-neutral-light/10 transition-all duration-200 hover:bg-neutral-dark/60 hover:border-brand-accent/30 focus:outline-none focus:ring-2 focus:ring-brand-accent/40 disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label={t('navigation.language')}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="font-heading font-semibold text-xs uppercase tracking-wider">
          {current.code}
        </span>
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 bg-neutral-dark border border-neutral-light/10 rounded-lg shadow-xl z-50 min-w-[160px] py-1">
          {LANGUAGE_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm font-body transition-colors hover:bg-brand-accent/20 ${
                lang.code === currentLanguage
                  ? 'text-brand-accent font-semibold'
                  : 'text-neutral-light'
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.nativeName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

